import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  finalize,
  first,
  skipWhile,
  switchMap,
  takeUntil,
  throwError,
} from "rxjs";
import { ITokens } from "src/app/epicare-public/login/domain/interfaces/login.interface";
import { LoginDialogComponent } from "src/app/epicare-public/login/views/login-dialog/login-dialog.component";
import { ConfigService } from "./config.service";
import { ConnectionEventsService } from "./connection-events.service";
import { SessionService } from "./session.service";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private baseHeader = new HttpHeaders({ "Content-Type": "application/json" });
  public refreshErrorMsg = "Session expired, need to reconnect";
  private logoutUrl = "user/logout";

  private loginDialogRef?: MatDialogRef<LoginDialogComponent>;
  private isRefreshing$ = new BehaviorSubject(false);
  private cancelRefreshFlow$ = new Subject<void>();
  private cancelPendingRequests$ = new Subject<void>();
  private suspendVerifyTokens = false;
  private _baseUrl: string;

  get baseUrl(): string {
    return this._baseUrl;
  }

  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private connectionEventsService: ConnectionEventsService,
    private configService: ConfigService
  ) {
    this._baseUrl = this.configService.apiUrl;
    this.connectionEventsService.logout$.subscribe(() => {
      this.cancelVerifyTokens();
    });
  }

  /**
   * Performs an authenticated GET request, automatically refreshing the access token and
   * retrying the request if it fails with a 401.
   * @param url Relative API endpoint to call.
   * @param params Optional query parameters to append to the request.
   * @param isAuthRequired Whether the request should include the access token and trigger the refresh flow on 401.
   * @return The response body.
   */
  public get(url: string, params?: Record<string, string | number | boolean>, isAuthRequired = true): Observable<unknown> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }
    const request = (url: string, body: unknown, options: any): Observable<unknown> => {
      return this.http.get(url, options);
    };

    return request(this.baseUrl + "/" + url, undefined, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401 && isAuthRequired) {
          return this.verifyTokensAndRedoRequest(request, url, undefined, options);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Performs an authenticated GET request expecting a blob response, returning the full HTTP
   * response and automatically refreshing the access token and retrying the request if it
   * fails with a 401.
   * @param url Relative API endpoint to call.
   * @param params Optional query parameters to append to the request.
   * @param isAuthRequired Whether the request should include the access token and trigger the refresh flow on 401.
   * @return The full HTTP response containing the blob body.
   */
  public getBlobAsFullResponse(
    url: string,
    params?: Record<string, string | number | boolean>,
    isAuthRequired = true
  ): Observable<unknown> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
      responseType: "blob",
      observe: "response",
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: any): Observable<unknown> => {
      return this.http.get(url, options);
    };

    return request(this.baseUrl + "/" + url, undefined, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401 && isAuthRequired) {
          return this.verifyTokensAndRedoRequest(request, url, undefined, options);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Performs an authenticated POST request, automatically refreshing the access token and
   * retrying the request if it fails with a 401.
   * @param url Relative API endpoint to call.
   * @param body Request payload.
   * @param headerParams Optional additional headers to send with the request.
   * @param isAuthRequired Whether the request should include the access token and trigger the refresh flow on 401.
   * @param params Optional query parameters to append to the request.
   * @param responseType Optional expected response type.
   * @return The response body.
   */
  public post(
    url: string,
    body: unknown,
    headerParams?: Record<string, string | number | boolean>,
    isAuthRequired = true,
    params?: Record<string, string | number | boolean>,
    responseType?: string
  ): Observable<unknown> {
    const options = {
      headers: new HttpHeaders(),
      params: new HttpParams(),
      responseType,
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (headerParams) {
      for (const k in headerParams) {
        if (headerParams[k]) {
          options.headers = options.headers.set(k, headerParams[k].toString());
        }
      }
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }
    const request = (url: string, body: unknown, options: any): Observable<unknown> => {
      return this.http.post(url, body, options);
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401 && isAuthRequired) {
          return this.verifyTokensAndRedoRequest(request, url, body, options);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Performs an authenticated PUT request, automatically refreshing the access token and
   * retrying the request if it fails with a 401.
   * @param url Relative API endpoint to call.
   * @param body Request payload.
   * @param isAuthRequired Whether the request should include the access token and trigger the refresh flow on 401.
   * @return The response body.
   */
  public put(url: string, body: unknown, isAuthRequired = true): Observable<unknown> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }
    const request = (url: string, body: unknown, options: any): Observable<unknown> => {
      return this.http.put(url, body, options);
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401 && isAuthRequired) {
          return this.verifyTokensAndRedoRequest(request, url, body, options);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Performs an authenticated PATCH request, automatically refreshing the access token and
   * retrying the request if it fails with a 401.
   * @param url Relative API endpoint to call.
   * @param body Request payload.
   * @param isAuthRequired Whether the request should include the access token and trigger the refresh flow on 401.
   * @param params Optional query parameters to append to the request.
   * @return The response body.
   */
  public patch(url: string, body: unknown, isAuthRequired = true, params?: Record<string, string | number | boolean>): Observable<unknown> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: any): Observable<unknown> => {
      return this.http.patch(url, body, options);
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401 && isAuthRequired) {
          return this.verifyTokensAndRedoRequest(request, url, body, options);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Performs an authenticated DELETE request, automatically refreshing the access token and
   * retrying the request if it fails with a 401.
   * @param url Relative API endpoint to call.
   * @param isAuthRequired Whether the request should include the access token and trigger the refresh flow on 401.
   * @return The response body.
   */
  public delete(url: string, isAuthRequired = true): Observable<unknown> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    const request = (url: string, body: unknown, options: any): Observable<unknown> => {
      return this.http.delete(url, options);
    };

    return request(this.baseUrl + "/" + url, undefined, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401 && isAuthRequired) {
          return this.verifyTokensAndRedoRequest(request, url, undefined, options);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Handles 401 authentication failures by either:
   * - waiting for an ongoing token refresh to complete, or
   * - triggering a refresh flow and retrying the original request.
   *
   * Depending on the refresh result, it may retry the request,
   * open a login dialog, redirect to login, or propagate an error.
   */
  private verifyTokensAndRedoRequest(
    request: any,
    url: string,
    body: unknown,
    options: { headers: HttpHeaders; params: HttpParams }
  ): Observable<unknown> {
    // Deal with suspended refreshes during handle-link
    if (this.suspendVerifyTokens) {
      return EMPTY;
    }

    if (this.isRefreshing$.value) {
      // Don't pipe logout, to avoid reconnection for the refresh, then directly log out
      if (url === this.logoutUrl) {
        return throwError(() => ({ message: "Logout cancelled" }));
      }
      // retry all the request when the refresh is done
      return this.isRefreshing$.pipe(
        skipWhile((v) => v),
        first(),
        takeUntil(this.cancelPendingRequests$),
        switchMap(() => {
          options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
          return request(this.baseUrl + "/" + url, body, options);
        })
      );
    } else {
      // token expired, we try to refresh it with the refresh token
      this.isRefreshing$.next(true);
      return this.refreshToken().pipe(
        first(),
        takeUntil(this.cancelRefreshFlow$),
        switchMap((newTokens) => {
          // token refreshed
          this.sessionService.accessToken = newTokens.accessToken as string;
          this.sessionService.refreshToken = newTokens.refreshToken as string;
          options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
          // retry the request
          return request(this.baseUrl + "/" + url, body, options).pipe(
            catchError((requestError) => {
              // Error while redoing the request (refresh itself was OK):
              // mark it so it's simply rethrown below, with no side effects.
              requestError.__isRedoRequestError = true;
              return throwError(() => requestError);
            })
          );
        }),
        catchError((error) => {
          if (error?.__isRedoRequestError) {
            delete error.__isRedoRequestError;
            // Just propagate the redo-request error, no logout/navigation.
            return throwError(() => error);
          }

          // From here on: this is really a refreshToken() failure.
          const refreshError = error;
          if (refreshError.status === 401) {
            if (url === this.logoutUrl) {
              this.connectionEventsService.notifyLogout();
            } else {
              this.loginDialogRef = this.dialog.open(LoginDialogComponent, {
                autoFocus: false,
                disableClose: true,
              });
              return this.loginDialogRef.afterClosed().pipe(
                first(),
                takeUntil(this.cancelRefreshFlow$),
                finalize(() => {
                  this.loginDialogRef = undefined;
                }),
                switchMap((loggedIn: boolean) => {
                  if (!loggedIn) {
                    return EMPTY;
                  }
                  options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
                  return request(this.baseUrl + "/" + url, body, options);
                })
              );
            }
            throw new Error(this.refreshErrorMsg);
          } else {
            // The refreshToken() call itself failed for a non-auth reason: session state is
            // no longer reliable, so log out instead of leaving the app stuck with a stale token.
            this.connectionEventsService.notifyLogout("common.error");
            throw refreshError;
          }
        }),
        finalize(() => {
          this.isRefreshing$.next(false);
        })
      );
    }
  }

  /**
   * Closes the login dialog and cancels ongoing token refresh flows and requests.
   */
  public cancelVerifyTokens(): void {
    this.loginDialogRef?.close(false);

    this.cancelRefreshFlow$.next();
    this.cancelPendingRequests$.next();
  }

  /**
   * Suspends token verification by cancelling any active refresh flow.
   * Must always be followed by resumeVerifyTokensFlow() to re-enable token handling.
   */
  public suspendVerifyTokensFlow(): void {
    this.cancelVerifyTokens();
    this.suspendVerifyTokens = true;
  }

  /**
   * Resumes token verification after it has been suspended.
   */
  public resumeVerifyTokensFlow(): void {
    this.suspendVerifyTokens = false;
  }

  /**
   * Requests a new access/refresh token pair using the current refresh token.
   * @return The newly issued tokens.
   */
  public refreshToken(): Observable<ITokens> {
    let headers = this.baseHeader;
    if (this.sessionService.refreshToken) {
      headers = headers.set("Authorization", "Bearer " + this.sessionService.refreshToken);
    }
    return this.http.post(this.baseUrl + "/user/auth/refresh", undefined, { headers }) as Observable<ITokens>;
  }
}
